import { createAppSlice } from "@/app/createAppSlice"
import { getReviews, createReview as createReviewApi, updateReview as updateReviewApi, deleteReview as deleteReviewApi } from "@/services/api"
import type { ReviewResponse, ReviewCreateRequest, ReviewUpdateRequest } from "@/types/review"

export type SiteReviewsState = {
  reviews: ReviewResponse[]
  status: "idle" | "loading" | "succeeded" | "failed"
  hasMore: boolean
  page: number
  error: string | null
}

const initialState: SiteReviewsState = {
  reviews: [],
  status: "idle",
  hasMore: true,
  page: 0,
  error: null,
}

export const siteReviewsSlice = createAppSlice({
  name: "siteReviews",
  initialState,
  reducers: create => ({
    resetReviews: create.reducer(state => {
      state.reviews = []
      state.page = 0
      state.hasMore = true
      state.status = "idle"
      state.error = null
    }),
    fetchReviews: create.asyncThunk(
      async ({ siteId, page, size = 10 }: { siteId: string; page: number; size?: number }) => {
        const response = await getReviews(siteId, page, size)
        return response
      },
      {
        pending: state => {
          state.status = "loading"
        },
        fulfilled: (state, action) => {
          state.status = "succeeded"
          const { content, last, number } = action.payload
          if (number === 0) {
            state.reviews = content
          } else {
            state.reviews = [...state.reviews, ...content]
          }
          state.hasMore = !last
          state.page = number + 1
        },
        rejected: state => {
          state.status = "failed"
          state.error = "리뷰를 불러오는데 실패했습니다."
        },
      },
    ),
    createReview: create.asyncThunk(
      async (request: ReviewCreateRequest) => {
        const response = await createReviewApi(request)
        return response
      },
      {
        fulfilled: (state, action) => {
          state.reviews = [action.payload, ...state.reviews]
        },
      },
    ),
    updateReview: create.asyncThunk(
      async ({ reviewId, request }: { reviewId: string; request: ReviewUpdateRequest }) => {
        const response = await updateReviewApi(reviewId, request)
        return response
      },
      {
        fulfilled: (state, action) => {
          const updated = action.payload
          state.reviews = state.reviews.map(r => (r.id === updated.id ? updated : r))
        },
      },
    ),
    deleteReview: create.asyncThunk(
      async (reviewId: string) => {
        await deleteReviewApi(reviewId)
        return reviewId
      },
      {
        fulfilled: (state, action) => {
          const deletedId = action.payload
          state.reviews = state.reviews.filter(r => r.id !== deletedId)
        },
      },
    ),
  }),
  selectors: {
    selectReviews: state => state.reviews,
    selectReviewsStatus: state => state.status,
    selectReviewsHasMore: state => state.hasMore,
  },
})

export const { resetReviews, fetchReviews, createReview, updateReview, deleteReview } = siteReviewsSlice.actions
export const { selectReviews, selectReviewsStatus, selectReviewsHasMore } = siteReviewsSlice.selectors